import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import {
  EVENT_LIST,
  EVENT_EDIT,
  EVENT_DELETE,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
  EVENT_REGISTRY,
  EVENT_REGISTRY_STATUS_UPDATE,
} from "../../routes/routes";
import { EVENT_DELETED } from "../../constant/constant";
import { NoDataFound, checkEmptyOrNull, hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import Select from "react-select";
import Form from "react-bootstrap/Form";
import { toast } from "react-toastify";
import Loader from "../../components/common/Loader"; // Import the loader component
import { Modal, Image } from "react-bootstrap";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

const EventsList = () => {
  // ## State Variable Declaration
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [refresh, setRefresh] = useState(false); // New state to trigger refresh
  const [countryOptions, setCountryOptions] = useState({});
  const [stateOptions, setStateOptions] = useState({});
  const [regionOptions, setRegionOptions] = useState({});
  const [chapterOptions, setChapterOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState({});
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [filter, setFilter] = useState([]);
  const [detailData, setDetailData] = useState({});
  const [detailsModal, setDetailModal] = useState(false);
  const [registryModal, setRegistryModal] = useState(false);
  const [registryData, setRegistryData] = useState({});

  const selectStateRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  const handleStateClear = () => {
    selectStateRef.current.clearValue();
    selectRegionRef.current.clearValue();
    selectChapterRef.current.clearValue();
  };

  const handleRegionClear = () => {
    selectRegionRef.current.clearValue();
    selectChapterRef.current.clearValue();
  };

  const handleChapterClear = () => {
    selectChapterRef.current.clearValue();
  };

  const downloadQrImage = async (imageUrl, fileName) => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl, { mode: "cors" });
      if (!response.ok) throw new Error("Failed to fetch QR image");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "qr-code.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback: open image in new tab if CORS blocks blob download
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = fileName || "qr-code.png";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const handleDeleteEventClick = async (clickInfo) => {
    try {
      axios
        .delete(EVENT_DELETE + clickInfo.event.id)
        .then((res) => {
          if (res.status === 200 && res.data.success) {
            toast.success(EVENT_DELETED);
            setRefresh((prevRefresh) => !prevRefresh);
            navigate("/events");
          }
        })
        .catch((err) => {
          if (err.response.status === 422) {
            toast.error(err.response.data.msg);
          }
        });
    } catch (error) {
      // Handle errors
      console.error(error);
    }
  };

  // Event handler for dates set
  const handleDatesSet = (dateInfo) => {
    const { start, end } = dateInfo;
    const from_date = moment(start).format("YYYY-MM-DD");
    const to_date = moment(end).format("YYYY-MM-DD");
    setFilter({ ...filter, from_date: from_date, to_date: to_date });
  };

  const auth_user = JSON.parse(localStorage.getItem("user"));
  const isSuperAdmin = auth_user?.user_type === 1 ? 1 : 0;
  const isRegionAdmin = auth_user?.user_type === 2 ? 1 : 0;
  const { hasPermission } = usePermission();

  const fetchEventRegistryData = async (id) => {
    try {
      axios
        .get(EVENT_REGISTRY + id + "/list")
        .then((res) => {
          if (res.status === 200 && res.data.success) {
            let responseData = res.data.data.registrys;
            setRegistryData(responseData);
          }
        })
        .finally(() => {
          setLoading(false); // Set loading to false after data is fetched
        });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const updateRegistryStatus = async (eventId, registryId, status) => {
    axios
      .post(EVENT_REGISTRY_STATUS_UPDATE + registryId, { status: status })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          toast.success(response.data.msg);
          fetchEventRegistryData(eventId);
        }
      });
  };

  const fetchEventData = async (id) => {
    try {
      axios.get(EVENT_EDIT + id).then((res) => {
        if (res.status === 200 && res.data.success) {
          let responseData = res.data.data;
          setDetailModal(true);
          setDetailData(responseData);
        }
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    try {
      axios
        .get(EVENT_LIST, {
          params: {
            isAdmin: 1,
            ...filter,
          },
        })
        .then((response) => {
          if (response.status === 200 && response.data.success) {
            setEvents(response.data.data.events);
          }
        });
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    const fetchData = async () => {
      try {
        // Fetch country data
        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        const fetchedCountryOptions = countryResponse.data.data;
        setCountryOptions(fetchedCountryOptions);

        // Fetch state data
        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedStateOptions = stateResponse.data.data;
        setStateOptions(fetchedStateOptions);

        // Fetch region data
        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedRegionOptions = regionResponse.data.data;
        setRegionOptions(fetchedRegionOptions);

        // Fetch chapter data
        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedChapterOptions = chapterResponse.data.data;
        setChapterOptions(fetchedChapterOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [refresh, filter]);

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Events List</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Events List</span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              {hasPermission("events-create") && (
                <div className="pt-btn">
                  <Link to={"/create/event"}>
                    <button
                      type="button"
                      className="btn rounded-pill btn-primary ibutton"
                    >
                      <span className="tf-icons bx bx-plus"></span>&nbsp; Add
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="card icard">
          <div className="container-fluid f_containter">
            <div className="row mb-4">
              {hasRole([1]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="country_id">
                    Country
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    name="country_id"
                    isClearable
                    options={countryOptions}
                    onChange={(selected) => {
                      handleStateClear();
                      setFilter({
                        ...filter,
                        country_id: selected?.value,
                        state_id: null,
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="state_id">
                    State
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    ref={selectStateRef}
                    name="state_id"
                    isClearable
                    options={stateOptions}
                    onChange={(selected) => {
                      handleRegionClear();
                      setFilter({
                        ...filter,
                        state_id: selected?.value,
                        region_id: null,
                        chapter_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="region_id">
                      Region
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      ref={selectRegionRef}
                      name="region_id"
                      isClearable
                      options={regionOptions}
                      onChange={(selected) => {
                        handleChapterClear();
                        setFilter({
                          ...filter,
                          region_id: selected?.value,
                          chapter_id: null,
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {hasRole([1, 6, 7, 2]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="chapter_id">
                      Chapter
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      ref={selectChapterRef}
                      name="chapter_id"
                      isClearable
                      options={chapterOptions}
                      onChange={(selected) =>
                        setFilter({ ...filter, chapter_id: selected?.value })
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="table-responsive text-nowrap">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={{
                left: "prev,next",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              firstDay="1"
              displayEventTime={true}
              allDaySlot={false}
              defaultView="month"
              initialView="dayGridMonth"
              themeSystem="bootstrap"
              events={events}
              eventDidMount={(info) => {
                // console.log(info.event);
                info.el.style.backgroundColor = info.event.backgroundColor;
                info.el.style.borderColor = info.event.borderColor;
                info.el.style.color = info.event.textColor;
              }}
              editable={false}
              // eventClick={(event) => {
              //   fetchEventData(event.event.id);
              // }}
              eventContent={(eventContent) => (
                <div className="fc-event-main-container">
                  <div
                    className="fc-event-title"
                    title={eventContent.event.title}
                  >
                    {eventContent.event.title}
                  </div>
                  <div className="fc-event-image" title="Event Image">
                    <Image
                      width={100}
                      // height={56}
                      src={eventContent.event.extendedProps.image_path}
                    />
                  </div>
                  <div className="fc-event-icons">
                    {hasPermission("events-list") && (
                      <button
                        className="badge bg-label-info1 border-0 pad-4"
                        data-bs-toggle="tooltip"
                        data-bs-offset="0,4"
                        data-bs-placement="top"
                        data-bs-html="true"
                        title="View"
                        data-bs-original-title="View"
                        onClick={() => {
                          fetchEventData(eventContent.event.id);
                        }}
                      >
                        <i className="bx bx-show"></i>
                      </button>
                    )}
                    {hasPermission("events-edit") &&
                      eventContent.event.extendedProps.event_status != 1 && (
                        <Link to={`/event/${eventContent.event.id}/edit`}>
                          <button
                            className="badge bg-label-success1 border-0 pad-4"
                            data-bs-toggle="tooltip"
                            data-bs-offset="0,4"
                            data-bs-placement="top"
                            data-bs-html="true"
                            title="Edit"
                            data-bs-original-title="Edit"
                          >
                            <i className="bx bx-pencil"></i>
                          </button>
                        </Link>
                      )}
                    {hasPermission("events-edit") && (
                      <button
                        className="badge bg-label-success1 border-0 pad-4"
                        data-bs-toggle="tooltip"
                        data-bs-offset="0,4"
                        data-bs-placement="top"
                        data-bs-html="true"
                        title="Attendance Registry"
                        data-bs-original-title="Attendance Registry"
                        onClick={() => {
                          setRegistryModal(true);
                          fetchEventRegistryData(eventContent.event.id);
                        }}
                      >
                        <i className="bx bx-user-check"></i>
                      </button>
                    )}
                    {hasPermission("events-delete") &&
                      eventContent.event.extendedProps.event_status != 1 && (
                        <button
                          className="badge bg-label-danger1 border-0 pad-4"
                          data-bs-toggle="tooltip"
                          data-bs-offset="0,4"
                          data-bs-placement="top"
                          data-bs-html="true"
                          title="Delete"
                          data-bs-original-title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmationModal(true);
                            setConfirmation({
                              message: "Are you sure, you want to delete?",
                              fn: () => handleDeleteEventClick(eventContent),
                            });
                          }}
                        >
                          <i className="bx bx-trash"></i>
                        </button>
                      )}
                  </div>
                </div>
              )}
              datesSet={handleDatesSet}
            />
          </div>
        </div>
      </div>

      <Modal
        show={confirmationModal}
        size="sm"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setConfirmationModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton className="no-border"></Modal.Header>
        <Modal.Body className="idel_popup">
          <div className="icon_holder">
            <div className="pop_del_icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="50"
                height="50"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1A4331"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </div>
          </div>
          <h6 className="text-center modal_title">{confirmation?.message}</h6>
          <div className="text-center">
            <button
              onClick={() => {
                setConfirmationModal(false);
                setConfirmation({});
              }}
              className="btn btn-danger btn-sm me-2 iclose_btn"
            >
              No
            </button>
            <button
              onClick={() => {
                confirmation.fn();
                setConfirmationModal(false);
                setConfirmation({});
              }}
              className="btn btn-success btn-sm isubmit_button"
            >
              Yes
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={detailsModal}
        size="l"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setDetailModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="modal_title1"
          >
            Event Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="card-body py-0">
            <div className="row">
              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Country</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.country_name)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">State</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.state_name)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Region</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.region_name)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Chapter</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.chapter_name)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Event Title</label>

                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.title)}
                  </span>
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-4">
                  <label className="form-label m-0">Event Image</label>
                  <span className="d-block font-weight-600">
                    <a
                      href={detailData?.image_path}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image
                        width={120}
                        // height={80}
                        src={detailData?.image_path}
                      />
                    </a>
                  </span>
                </div>
              </div>

              {detailData?.qrcode_path && (
                <div className="col-md-6">
                  <div className="mb-4">
                    <label className="form-label m-0">UPI QR Code</label>
                    <span className="d-block font-weight-600">
                      <a
                        href={detailData?.qrcode_path}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          width={100}
                          height={100}
                          src={detailData?.qrcode_path}
                        />
                      </a>
                    </span>
                    <button
                      type="button"
                      className="badge bg-label-primary border-0 mt-2"
                      title="Download UPI QR"
                      onClick={() =>
                        downloadQrImage(
                          detailData.qrcode_path,
                          `event-${detailData?.id || "upi"}-upi-qr.png`
                        )
                      }
                    >
                      <i className="bx bx-download me-1"></i>
                      Download
                    </button>
                  </div>
                </div>
              )}

              {detailData?.attendance_qrcode_path && (
                <div className="col-md-6">
                  <div className="mb-4">
                    <label className="form-label m-0">Event QR</label>
                    <span className="d-block font-weight-600">
                      <a
                        href={detailData?.attendance_qrcode_path}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Image
                          width={100}
                          height={100}
                          src={detailData?.attendance_qrcode_path}
                        />
                      </a>
                    </span>
                    <button
                      type="button"
                      className="badge bg-label-primary border-0 mt-2"
                      title="Download Event QR"
                      onClick={() =>
                        downloadQrImage(
                          detailData.attendance_qrcode_path,
                          `event-${detailData?.id || "attendance"}-qr.png`
                        )
                      }
                    >
                      <i className="bx bx-download me-1"></i>
                      Download
                    </button>
                  </div>
                </div>
              )}

              {detailData?.payee_upi_id && (
                <>
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label m-0">
                        Payee Address (Merchant UPI ID)
                      </label>
                      <span className="d-block font-weight-600">
                        {checkEmptyOrNull(detailData.payee_upi_id)}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label m-0">Payee Name</label>
                      <span className="d-block font-weight-600">
                        {checkEmptyOrNull(detailData.payee_name)}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label m-0">Amount to be paid</label>
                      <span className="d-block font-weight-600">
                        {detailData.payment_amount != null
                          ? Number(detailData.payment_amount).toFixed(2)
                          : "-"}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label m-0">Currency</label>
                      <span className="d-block font-weight-600">
                        {checkEmptyOrNull(detailData.payment_currency || "INR")}
                      </span>
                    </div>
                  </div>
                </>
              )}

              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Event Date</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.event_date)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">From Time</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.from_time)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">To Time</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.to_time)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-4">
                  <label className="form-label m-0">Created By</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.created_by_user_name)}
                  </span>
                </div>
              </div>

              <div className="col-md-12">
                <div className="mb-4">
                  <label className="form-label m-0">Event Description</label>
                  <span className="d-block font-weight-600">
                    {checkEmptyOrNull(detailData.event_description)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary ibutton"
            data-bs-dismiss="modal"
            onClick={() => setDetailModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={registryModal}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setRegistryModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="modal_title1"
          >
            Event Attended Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive text-nowrap">
            <table className="table table-striped itable">
              <thead>
                <tr>
                  <th style={{ textAlign: "center", width: "10%" }}>Sl. No.</th>
                  <th style={{ textAlign: "center", width: "60%" }}>User</th>
                  <th style={{ textAlign: "center", width: "30%" }}>
                    Attended Status
                  </th>
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {loading ? (
                  <Loader loading={loading} colspan="3" />
                ) : registryData.length > 0 ? (
                  registryData.map((registry, i) => (
                    <tr key={i}>
                      <td>{registry?.sno}</td>
                      <td>
                        <span className="table_img pr-2">
                          <img src={registry?.user_profile_image} alt="icon" />
                        </span>
                        {registry?.user_name}
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          id={`custom-switch-${i}`}
                          value="1"
                          disabled={!hasPermission("events-edit")}
                          checked={registry?.is_attended === 1 ? true : false}
                          label=""
                          onChange={(event) => {
                            const statusVal = event.target.checked ? 1 : 0;
                            updateRegistryStatus(
                              registry?.event_id,
                              registry?.id,
                              statusVal,
                            );
                          }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <NoDataFound colspan="3" message="No Data Found" />
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary ibutton"
            data-bs-dismiss="modal"
            onClick={() => setRegistryModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EventsList;
